import { useParams, useSearchParams, useNavigate } from "react-router-dom"
import { CheckCircle2, Loader2, Edit, UserMinus, Pencil, Trash2, Plus } from "lucide-react"
import { toast } from "sonner"
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

import { APP_NAME } from "@/lib/appConfig"
import { useLease, useUnassignLease } from "@/features/leases/hooks/useLeases"
import { useRentRecords, useUpdateRentRecord, useDeleteRentRecord, useCreateRentRecord } from "@/features/finances/hooks/useFinances"
import { LeaseForm } from "@/features/leases/components/LeaseForm"
import { PageHeader } from "@/components/shared/PageHeader"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { ConfirmDialog } from "@/components/shared/ConfirmDialog"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useCurrency } from "@/hooks/useCurrency"
import type { RentRecord } from "@/features/finances/services/financesService"
import type { Lease } from "@rentbase/shared"

const RENT_STATUSES = ["pending", "paid", "partial", "late", "unpaid", "cancelled"] as const

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

// ─── Edit Rent Record Sheet ───────────────────────────────────────────────────

const editRentSchema = z.object({
  amount: z.coerce.number().positive("Amount must be positive"),
  status: z.enum(RENT_STATUSES),
  due_date: z.string().min(1),
  paid_date: z.string().optional(),
  payment_method: z.string().optional(),
  notes: z.string().optional(),
})
type EditRentFormValues = z.infer<typeof editRentSchema>

function EditRentRecordSheet({
  open,
  onOpenChange,
  record,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  record: RentRecord | null
}) {
  const updateRentRecord = useUpdateRentRecord()

  const form = useForm<EditRentFormValues>({
    resolver: zodResolver(editRentSchema),
    defaultValues: { amount: 0, status: "pending", due_date: "", paid_date: "", payment_method: "", notes: "" },
  })

  useEffect(() => {
    if (open && record) {
      form.reset({
        amount: record.amount,
        status: record.status,
        due_date: record.due_date,
        paid_date: record.paid_date ?? "",
        payment_method: "",
        notes: record.notes ?? "",
      })
    }
  }, [open, record])

  function onSubmit(values: EditRentFormValues) {
    if (!record) return
    const payload: Record<string, unknown> = {
      amount: values.amount,
      status: values.status,
      due_date: values.due_date,
    }
    if (values.paid_date) payload.paid_date = values.paid_date
    if (values.payment_method) payload.payment_method = values.payment_method
    if (values.notes) payload.notes = values.notes

    updateRentRecord.mutate({ id: record.id, input: payload }, {
      onSuccess: () => { toast.success("Rent record updated"); onOpenChange(false) },
      onError: () => toast.error("Failed to update record"),
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>Edit Rent Record</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="amount" render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl><Input type="number" step="0.01" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="due_date" render={({ field }) => (
                <FormItem>
                  <FormLabel>Due Date *</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="status" render={({ field }) => (
              <FormItem>
                <FormLabel>Status *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    {RENT_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="paid_date" render={({ field }) => (
              <FormItem>
                <FormLabel>Paid Date</FormLabel>
                <FormControl><Input type="date" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="payment_method" render={({ field }) => (
              <FormItem>
                <FormLabel>Payment Method</FormLabel>
                <FormControl><Input placeholder="e.g. Bank transfer, Cash..." {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl><Textarea placeholder="Optional notes..." rows={2} className="resize-none" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <Button type="submit" className="w-full h-12 rounded-xl" disabled={updateRentRecord.isPending}>
              {updateRentRecord.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Changes"}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// ─── Add Rent Record Sheet (lease-scoped) ────────────────────────────────────

const addRentSchema = z.object({
  month: z.string().min(1, "Month is required"),
  amount: z.coerce.number().positive("Amount must be positive"),
  currency: z.string().min(1),
  status: z.enum(RENT_STATUSES),
  paid_date: z.string().optional(),
  payment_method: z.string().optional(),
  notes: z.string().optional(),
})
type AddRentFormValues = z.infer<typeof addRentSchema>

function AddLeaseRentRecordSheet({
  open,
  onOpenChange,
  leaseId,
  monthlyRent,
  currency,
  paymentDueDay,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  leaseId: string
  monthlyRent: number
  currency: string
  paymentDueDay: number
}) {
  const createRentRecord = useCreateRentRecord()
  const { data: existingRecords } = useRentRecords(leaseId)

  const form = useForm<AddRentFormValues>({
    resolver: zodResolver(addRentSchema),
    defaultValues: {
      month: new Date().toISOString().slice(0, 7),
      amount: monthlyRent,
      currency,
      status: "pending",
      paid_date: "",
      payment_method: "",
      notes: "",
    },
  })

  const watchedMonth = form.watch("month")
  const watchedStatus = form.watch("status")

  useEffect(() => {
    if (open) {
      form.reset({
        month: new Date().toISOString().slice(0, 7),
        amount: monthlyRent,
        currency,
        status: "pending",
        paid_date: "",
        payment_method: "",
        notes: "",
      })
    }
  }, [open])

  const duplicateWarning = (() => {
    if (!watchedMonth || !existingRecords) return false
    const [y, m] = watchedMonth.split("-").map(Number)
    const dueM = m === 12 ? 1 : m + 1
    const dueY = m === 12 ? y + 1 : y
    const duePfx = `${dueY}-${String(dueM).padStart(2, "0")}`
    return existingRecords.some((r) => r.due_date?.startsWith(duePfx))
  })()

  function onSubmit(values: AddRentFormValues) {
    const [yearStr, monthStr] = values.month.split("-")
    const year = parseInt(yearStr)
    const monthIdx = parseInt(monthStr) - 1
    const dueMonthIdx = monthIdx === 11 ? 0 : monthIdx + 1
    const dueYear = monthIdx === 11 ? year + 1 : year
    const lastDay = new Date(dueYear, dueMonthIdx + 1, 0).getDate()
    const actual = Math.min(paymentDueDay, lastDay)
    const due_date = `${dueYear}-${String(dueMonthIdx + 1).padStart(2, "0")}-${String(actual).padStart(2, "0")}`

    const payload: Record<string, unknown> = {
      lease_id: leaseId,
      due_date,
      amount: values.amount,
      currency: values.currency,
      status: values.status,
    }
    if (values.paid_date) payload.paid_date = values.paid_date
    if (values.payment_method) payload.payment_method = values.payment_method
    if (values.notes) payload.notes = values.notes

    createRentRecord.mutate(payload, {
      onSuccess: () => { toast.success("Rent record added"); onOpenChange(false) },
      onError: () => toast.error("Failed to add rent record"),
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>Add Rent Record</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField control={form.control} name="month" render={({ field }) => (
              <FormItem>
                <FormLabel>Month *</FormLabel>
                <FormControl><Input type="month" {...field} /></FormControl>
                {duplicateWarning && (
                  <p className="text-xs text-amber-600 font-medium mt-1">
                    A rent record already exists for this month. Submitting will create an additional one.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="amount" render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl><Input type="number" step="0.01" placeholder="0.00" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="currency" render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <FormControl><Input maxLength={3} {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="status" render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    {RENT_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            {(watchedStatus === "paid" || watchedStatus === "partial") && (
              <>
                <FormField control={form.control} name="paid_date" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Paid Date</FormLabel>
                    <FormControl><Input type="date" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="payment_method" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Method</FormLabel>
                    <FormControl><Input placeholder="e.g. Bank transfer, Cash..." {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </>
            )}

            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl><Textarea placeholder="Optional notes..." rows={2} className="resize-none" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <Button type="submit" className="w-full h-12 rounded-xl" disabled={createRentRecord.isPending}>
              {createRentRecord.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</>
                : "Add Record"}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LeaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isUnassignOpen, setIsUnassignOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<RentRecord | null>(null)
  const [editRecordOpen, setEditRecordOpen] = useState(false)
  const [deleteRecordId, setDeleteRecordId] = useState<string | null>(null)
  const [addRecordOpen, setAddRecordOpen] = useState(false)

  const { data: lease, isLoading, isError } = useLease(id ?? "")
  const { data: rentRecords } = useRentRecords(id)
  const updateRentRecord = useUpdateRentRecord()
  const deleteRentRecord = useDeleteRentRecord()
  const unassignLease = useUnassignLease()
  const { format } = useCurrency()

  useEffect(() => {
    const prev = document.title
    document.title = `Lease | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  // Auto-open edit sheet if ?edit=true is in URL
  useEffect(() => {
    if (searchParams.get("edit") === "true") {
      setIsEditOpen(true)
      navigate(".", { replace: true })
    }
  }, [searchParams, navigate])

  if (isLoading) return <ListSkeleton count={4} />

  if (isError || !lease) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Lease" back />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Lease not found.</p>
        </div>
      </div>
    )
  }

  const l = lease as unknown as {
    tenant?: { full_name?: string }
    property?: { name?: string }
    status?: string
    start_date?: string
    end_date?: string
    monthly_rent?: number
    currency?: string
    deposit?: number
    lease_type?: string
    notes?: string
    payment_due_day?: number
  }

  function markPaid(record: RentRecord) {
    updateRentRecord.mutate(
      { id: record.id, input: { status: "paid", paid_date: new Date().toISOString() } },
      {
        onSuccess: () => toast.success("Marked as paid"),
        onError: () => toast.error("Failed to update"),
      }
    )
  }

  function handleUnassign() {
    if (!id) return
    unassignLease.mutate(id, {
      onSuccess: () => {
        setIsUnassignOpen(false);
        toast.success("Tenant unassigned effectively.");
        navigate("/leases", { replace: true });
      },
      onError: (err: any) => {
        setIsUnassignOpen(false);
        toast.error(err.response?.data?.error || "Failed to unassign tenant.");
      }
    })
  }

  function handleDeleteRecord() {
    if (!deleteRecordId) return
    deleteRentRecord.mutate(deleteRecordId, {
      onSuccess: () => { toast.success("Rent record deleted"); setDeleteRecordId(null) },
      onError: () => { toast.error("Failed to delete record"); setDeleteRecordId(null) },
    })
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Lease" back />

      <div className="flex-1 overflow-y-auto pb-nav">
        {/* Summary */}
        <div className="px-4 pt-4 pb-3">
          <Card className="rounded-2xl border-border">
            <CardContent className="p-4 space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-base">
                    {l.tenant?.full_name ?? "Unknown tenant"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {l.property?.name ?? "Unknown property"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={l.status ?? "active"} />
                </div>
              </div>
              <div className="flex gap-2 pt-3 border-t border-border mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 h-9 rounded-xl gap-1 text-xs"
                  onClick={() => setIsEditOpen(true)}
                >
                  <Edit className="h-3.5 w-3.5" />
                  Edit Lease
                </Button>
                {l.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-9 rounded-xl gap-1 text-xs border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"
                    onClick={() => setIsUnassignOpen(true)}
                    disabled={unassignLease.isPending}
                  >
                    {unassignLease.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5" />}
                    Unassign Tenant
                  </Button>
                )}
              </div>
              <div className="pt-2 border-t border-border grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Start</p>
                  <p className="font-medium">{formatDate(l.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">End</p>
                  <p className="font-medium">{formatDate(l.end_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Monthly Rent</p>
                  <p className="font-bold text-base">
                    {l.monthly_rent != null ? format(l.monthly_rent) : "—"}
                  </p>
                </div>
                {l.deposit != null && (
                  <div>
                    <p className="text-xs text-muted-foreground">Deposit</p>
                    <p className="font-medium">{format(l.deposit)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="px-4">
          <Tabs defaultValue="rent">
            <TabsList className="w-full h-10 rounded-xl mb-4">
              <TabsTrigger value="rent" className="flex-1 rounded-lg text-sm">
                Rent Records
              </TabsTrigger>
              <TabsTrigger value="details" className="flex-1 rounded-lg text-sm">
                Details
              </TabsTrigger>
            </TabsList>

            <TabsContent value="rent">
              <div className="flex justify-end mb-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-xl gap-1 text-xs"
                  onClick={() => setAddRecordOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Record
                </Button>
              </div>
              {!rentRecords?.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No rent records yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {rentRecords.map((record) => {
                    const isPaid = record.status === "paid"
                    // Derive billing month label from due_date (due is next month, so billing = one month before)
                    const billingLabel = (() => {
                      if (!record.due_date) return "—"
                      const d = new Date(record.due_date)
                      d.setMonth(d.getMonth() - 1)
                      return d.toLocaleDateString(undefined, { month: "long", year: "numeric" })
                    })()
                    return (
                      <div key={record.id} className="rounded-2xl border border-border/60 bg-card overflow-hidden">
                        {/* Card header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
                          <div>
                            <p className="text-sm font-bold leading-tight">{billingLabel}</p>
                            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mt-0.5">Billing Period</p>
                          </div>
                          <div className="flex items-center gap-2.5">
                            <p className="text-base font-extrabold text-foreground">{format(record.amount)}</p>
                            <StatusBadge status={record.status} />
                          </div>
                        </div>

                        {/* Data columns */}
                        <div className="grid grid-cols-2 divide-x divide-border/50">
                          <div className="px-4 py-2.5">
                            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">Due Date</p>
                            <p className="text-xs font-semibold text-foreground">{formatDate(record.due_date)}</p>
                          </div>
                          <div className="px-4 py-2.5">
                            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">Paid Date</p>
                            <p className="text-xs font-semibold text-foreground">{formatDate(record.paid_date)}</p>
                          </div>
                        </div>

                        {/* Action row */}
                        <div className="flex gap-2 px-4 py-2.5 border-t border-border/50">
                          {!isPaid && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 h-8 rounded-lg text-xs gap-1"
                              onClick={() => markPaid(record)}
                              disabled={updateRentRecord.isPending}
                            >
                              {updateRentRecord.isPending ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" />
                              )}
                              Mark Paid
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-lg text-xs gap-1"
                            onClick={() => { setEditingRecord(record); setEditRecordOpen(true) }}
                          >
                            <Pencil className="h-3 w-3" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-lg text-xs gap-1 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setDeleteRecordId(record.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="details">
              <Card className="rounded-2xl border-border">
                <CardContent className="p-4 space-y-3">
                  {[
                    { label: "Lease Type", value: l.lease_type },
                    { label: "Currency", value: l.currency },
                    { label: "Notes", value: l.notes },
                  ]
                    .filter((r) => r.value)
                    .map((row) => (
                      <div key={row.label} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{row.label}</span>
                        <span className="font-medium">{row.value}</span>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <LeaseForm
        lease={lease as unknown as Lease}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
      <ConfirmDialog
        open={isUnassignOpen}
        onOpenChange={setIsUnassignOpen}
        title="Unassign Tenant"
        description="Are you sure you want to unassign this tenant? The lease will be terminated immediately, making the property vacant again. Historical rent records will remain intact."
        confirmLabel="Unassign"
        onConfirm={handleUnassign}
      />
      <EditRentRecordSheet
        open={editRecordOpen}
        onOpenChange={setEditRecordOpen}
        record={editingRecord}
      />
      <AddLeaseRentRecordSheet
        open={addRecordOpen}
        onOpenChange={setAddRecordOpen}
        leaseId={id ?? ""}
        monthlyRent={l.monthly_rent ?? 0}
        currency={l.currency ?? "EUR"}
        paymentDueDay={l.payment_due_day ?? 1}
      />
      <ConfirmDialog
        open={!!deleteRecordId}
        onOpenChange={(open) => { if (!open) setDeleteRecordId(null) }}
        title="Delete Rent Record"
        description="This will permanently delete this rent record. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDeleteRecord}
      />
    </div>
  )
}
